// frontend/src/utils/apiErrorHandler.ts
import { toast } from './toast';
import { AxiosError } from 'axios';

interface ApiError {
  message?: string;
  code?: string;
  error?: {
    message: string;
    code: string;
    status: number;
  };
}

/**
 * Utility to handle API errors consistently across the application
 * @param error The caught error from an API request
 * @param defaultMessage Default message to show if no specific error message is available
 */
export const handleApiError = (error: unknown, defaultMessage: string): void => {
  // Handle Axios errors
  if (isAxiosError(error)) {
    const responseData = error.response?.data as ApiError | undefined;
  
    // Unterstützt beide Formate
    const errorMessage = responseData?.error?.message || responseData?.message;
    const errorCode = responseData?.error?.code || responseData?.code;
    
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    
    // Registration deadline passed or competition closed
    if (error.response?.status === 403 && responseData?.message?.includes('Meldeschluss')) {
      toast.error('Der Meldeschluss für diesen Wettkampf ist bereits abgelaufen.');
      return;
    }
    
    // Registration limit reached
    if (error.response?.status === 400 && responseData?.message?.includes('Maximale Anzahl')) {
      toast.error(responseData.message);
      return;
    }
    
    // Unauthorized
    if (error.response?.status === 401) {
      toast.error('Nicht autorisiert. Bitte melde Dich erneut an.');
      return;
    }
    
    // Permission denied
    if (error.response?.status === 403) {
      toast.error('Du hast keine Berechtigung für diese Aktion.');
      return;
    }
    
    // Entity not found
    if (error.response?.status === 404) {
      toast.error('Die angeforderte Ressource wurde nicht gefunden.');
      return;
    }
    
    // Use custom error message from API if available
    if (responseData?.message) {
      toast.error(responseData.message);
      return;
    }
    
    // Server error
    if (error.response?.status && error.response.status >= 500) {
      toast.error('Ein Serverfehler ist aufgetreten. Bitte versuche es später noch einmal.');
      return;
    }
  }
  
  // For any other errors, use the default message
  toast.error(defaultMessage); 
};

/**
 * Type guard to check if an error is an AxiosError
 */
function isAxiosError(error: unknown): error is AxiosError {
  return (error as AxiosError)?.isAxiosError === true;
}

/**
 * Display success toast with a message
 */
export const showSuccess = (message: string): void => {
  toast.success(message);
};